// KPI Dashboard logic, kept apart from the React tabs so it can be tested.
// No fixed targets: every employee who opened a contract in the period appears on their own, and is read
// against the actual work of the period (the team average, their own previous period).

import { parseExcelDate } from '../../utils/contractDateUtils';
import { isInvygoNumber } from '../../services/contractRules';

const DAY = 86400000;

/** The month an old "I" contract number carries (I2509… -> "09"), a hint for its ambiguous M/D dates.
 *  Newer numbers (B2609000D04…) get none: the engine writes their dates as DD/MM, and a hint would misread
 *  a return on 09/10 (9 October) as 10 September. */
export function monthHintOf(contractNo) {
    const m = String(contractNo || '').trim().toUpperCase().match(/^I(\d{2})(\d{2})/);
    if (!m) return null;
    const month = Number(m[2]);
    return month >= 1 && month <= 12 ? m[2] : null;
}

/**
 * A contract date, its format read from the text itself (checked on all 60,000 contracts, 25 Sep 2026):
 *  - "DD/MM/YYYY HH:MM" (24 h, zero-padded): what the engine writes from Ejar - day first;
 *  - "M/D/YYYY h:mm:ss AM|PM [+04:00]": the old airport export - month first (37,730 of them, not one
 *    day-first; 22,356 prove it with a second part above 12).
 * The old rule guessed from the contract number's month and misread e.g. a return on 09/10 of an "I2609"
 * contract as 10 September. Anything else (Excel serials, ISO) goes to the general parser.
 */
export function parseContractDate(value) {
    if (value == null || value === '') return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const s = String(value).trim();
    let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)/i);
    if (m) {
        let h = Number(m[4]) % 12;
        if (m[7].toUpperCase() === 'PM') h += 12;
        const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]), h, Number(m[5]), Number(m[6] || 0));
        return Number.isNaN(d.getTime()) ? null : d;
    }
    m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (m) {
        const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0));
        return Number.isNaN(d.getTime()) || Number(m[2]) > 12 ? null : d;
    }
    return parseExcelDate(s);
}

/** An Invygo contract: branch code D04 in the Ejar number (B2609000D04462728), or the Invygo branch. */
export function isInvygo(contractNo, bookingNo, rawBranch) {
    // contract B2609000D04…, booking B26000D04…: the app's one branch-code reader (DUP-04)
    return isInvygoNumber(contractNo) || isInvygoNumber(bookingNo)
        || String(rawBranch || '').toLowerCase().includes('invygo');
}

/** One name per branch; "Business Bay - Invygo" is Invygo, not Business Bay. */
export function normalizeBranch(raw) {
    if (!raw) return 'Unassigned';
    const name = String(raw).toUpperCase();
    if (name.includes('INVYGO')) return 'Invygo';
    if (name.includes('TERMINAL 1') || name.includes('TERMENAL 1') || /\bT1\b/.test(name)) return 'Terminal 1';
    if (name.includes('TERMINAL 2') || name.includes('TERMENAL 2') || /\bT2\b/.test(name)) return 'Terminal 2';
    if (name.includes('BUSINESS BAY') || name.includes('BUSINESSBAY')) return 'Business Bay';
    return String(raw);
}

export const BRANCH_SHORT = { 'Terminal 1': 'T1', 'Terminal 2': 'T2', 'Business Bay': 'B.B', Invygo: 'INV' };

/** Does a branch pass the filter ("All", "Airport" = T1 + T2, or one branch)? */
export function branchMatch(branch, filter) {
    if (!filter || filter === 'All') return true;
    if (filter === 'Airport') return branch === 'Terminal 1' || branch === 'Terminal 2';
    return branch === filter;
}

// The engine's revenue report exists for our four Dubai branches only (T1, T2, Business Bay, Invygo), the
// airport pair and all of them. Any other branch or city has no report: the Revenue tab says so instead of
// showing the whole company's revenue under that heading (audit MON-22).
export const REVENUE_BRANCH_CODE = { All: 'all', Airport: 'airport', 'Terminal 1': 'T1', 'Terminal 2': 'T2', 'Business Bay': 'BB', Invygo: 'INV' };
export const REVENUE_CITIES = ['All', 'Dubai'];

/** The engine's branch code for the chosen branch + city, or null when there is no revenue report for them. */
export function revenueBranchCode({ branch = 'All', city = 'All' } = {}) {
    if (!REVENUE_CITIES.includes(city || 'All')) return null;
    return REVENUE_BRANCH_CODE[branch || 'All'] || null;
}

// What each KPI tab reads from Firestore. The Revenue tab reads only the engine's report, so it no longer
// waits for every contract / airport / archive / fleet collection (audit MON-20).
export const KPI_TAB_DATA = {
    overview: [],                // THE Overview: the engine's answer (pages/overview), no Firestore list
    ops: ['ops'],
    employees: ['ops', 'excluded'],
    fleet: ['fleet', 'ops'],
    contracts: ['ops'],
    financial: [],
    settings: ['staffMappings'],
};

/** The data sets a tab needs that are not loaded yet. */
export function missingSets(tab, loaded = {}) {
    return (KPI_TAB_DATA[tab] || []).filter((k) => !loaded[k]);
}

/** {s, e} in ms for a YYYY-MM-DD period, both days included (local time). */
export function periodBounds(from, to) {
    const s = from ? new Date(`${from}T00:00:00`).getTime() : -Infinity;
    const e = to ? new Date(`${to}T23:59:59.999`).getTime() : Infinity;
    return { s, e };
}

export const dayKey = (ms) => {
    const d = new Date(ms);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Number of days in the period (at least 1). */
export function periodDays(from, to) {
    const { s, e } = periodBounds(from, to);
    if (!Number.isFinite(s) || !Number.isFinite(e)) return 1;
    return Math.max(1, Math.round((e + 1 - s) / DAY));
}

/** The period of the same length just before this one (for "vs previous"). */
export function previousPeriod(from, to) {
    const n = periodDays(from, to);
    const s = new Date(`${from}T00:00:00`);
    const pe = new Date(s.getFullYear(), s.getMonth(), s.getDate() - 1);
    const ps = new Date(s.getFullYear(), s.getMonth(), s.getDate() - n);
    return { from: dayKey(ps.getTime()), to: dayKey(pe.getTime()) };
}

/**
 * The first day of contracts a period needs when the contracts come from the store (USE_CONTRACTS_V2, which serves
 * the contracts open now or returned since a day - not all of them): the period before this one, and 31 days
 * before its end (the 30-day chart, the 14-day line, the week-before tile), whichever is earlier. No start: a year.
 */
export function opsSinceFor(from, to) {
    const end = to ? new Date(`${to}T00:00:00`) : new Date();
    if (Number.isNaN(end.getTime())) return undefined;
    const monthBefore = dayKey(new Date(end.getFullYear(), end.getMonth(), end.getDate() - 31).getTime());
    if (!from) return dayKey(new Date(end.getFullYear() - 1, end.getMonth(), end.getDate()).getTime());
    const prev = previousPeriod(from, to || from).from;
    return prev < monthBefore ? prev : monthBefore;
}

const live = (op) => op.operationStatus !== 'Cancelled';

/** Contracts opened (picked up) in the period, in the branch. */
export function openedIn(ops, { from, to, branch }) {
    const { s, e } = periodBounds(from, to);
    return ops.filter((op) => live(op) && op.pickupAt != null && op.pickupAt >= s && op.pickupAt <= e && branchMatch(op.branch, branch));
}

/** Contracts closed (returned) in the period, in the branch. */
export function closedIn(ops, { from, to, branch }) {
    const { s, e } = periodBounds(from, to);
    return ops.filter((op) => op.operationStatus === 'Closed' && op.dropoffAt != null && op.dropoffAt >= s && op.dropoffAt <= e && branchMatch(op.branch, branch));
}

/** Contracts on the road at the end of the period (picked up by then, not returned by then). */
export function activeAt(ops, { to, branch }) {
    const { e } = periodBounds(null, to);
    return ops.filter((op) => live(op) && op.pickupAt != null && op.pickupAt <= e && branchMatch(op.branch, branch)
        && (op.operationStatus === 'In-Progress' || (op.operationStatus === 'Closed' && op.dropoffAt != null && op.dropoffAt > e)));
}

const change = (now, before) => (before > 0 ? Math.round(((now - before) / before) * 100) : null);

/** The Operations tab headline: opened / closed / on the road, per day, and vs the previous period. */
export function opsSummary(ops, filters) {
    const opened = openedIn(ops, filters);
    const closed = closedIn(ops, filters);
    const active = activeAt(ops, filters);
    const days = periodDays(filters.from, filters.to);
    const prev = openedIn(ops, { ...filters, ...previousPeriod(filters.from, filters.to) });
    const byType = {};
    opened.forEach((op) => { byType[op.type || 'Daily'] = (byType[op.type || 'Daily'] || 0) + 1; });
    const byBranch = {};
    opened.forEach((op) => { byBranch[op.branch || 'Unassigned'] = (byBranch[op.branch || 'Unassigned'] || 0) + 1; });
    return {
        opened, closed, active, days,
        perDay: Math.round((opened.length / days) * 10) / 10,
        prevOpened: prev.length,
        change: change(opened.length, prev.length),
        byType: Object.entries(byType).sort((a, b) => b[1] - a[1]),
        byBranch: Object.entries(byBranch).sort((a, b) => b[1] - a[1]),
    };
}

/** Contracts opened per day: the period itself, or the 30 days up to its end when it is shorter than a week. */
export function dailySeries(ops, { from, to, branch }) {
    const n = periodDays(from, to) < 7 ? 30 : Math.min(periodDays(from, to), 92);
    const end = new Date(`${to}T00:00:00`);
    const keys = [];
    for (let i = n - 1; i >= 0; i--) keys.push(dayKey(new Date(end.getFullYear(), end.getMonth(), end.getDate() - i).getTime()));
    const counts = Object.fromEntries(keys.map((k) => [k, 0]));
    openedIn(ops, { from: keys[0], to, branch }).forEach((op) => {
        const k = dayKey(op.pickupAt);
        if (k in counts) counts[k] += 1;
    });
    const series = Object.entries(counts);
    const worked = series.filter(([, c]) => c > 0);
    const avg = worked.length ? worked.reduce((sum, [, c]) => sum + c, 0) / worked.length : 0;
    return { series, avg: Math.round(avg * 10) / 10, from: keys[0], to };
}

/** Standing against the team: the employee's contracts vs the team average for the same period. */
export function standingOf(index) {
    if (index >= 1.15) return { label: 'Above average', tone: 'success' };
    if (index >= 0.85) return { label: 'Average', tone: 'accent' };
    return { label: 'Below average', tone: 'warning' };
}

/** The row a contract counts for: its employee, or its branch when the name is on the "not counted as
 *  employees" list (left, or a manager who does not open contracts) or there is no name at all. */
export function ownerOf(op, excluded) {
    const name = String(op.createdBy || '').trim();
    const raw = String(op.createdByRaw || '').trim();
    if (!name || name === 'System' || name === 'Unknown' || excluded.has(name) || (raw && excluded.has(raw))) {
        return { name: `${BRANCH_SHORT[op.branch] || op.branch || 'Unassigned'} branch`, isBranch: true };
    }
    return { name, isBranch: false };
}

/**
 * One row per employee who opened a contract in the period (in the branch): opened, still open, closed,
 * working days, per working day, where they worked, the contract mix, share of the team, vs the team
 * average and vs their own previous period. Nobody is listed by hand, nobody is set a target.
 * Contracts of names on the excluded list go to a row for the branch they were opened in; branch rows
 * are not people, so they stay out of the team average.
 */
export function employeeStats(ops, filters, excludedNames = []) {
    const excluded = new Set((excludedNames || []).map((n) => String(n).trim()).filter(Boolean));
    const opened = openedIn(ops, filters);
    const prev = openedIn(ops, { ...filters, ...previousPeriod(filters.from, filters.to) });
    const prevBy = {};
    prev.forEach((op) => { const n = ownerOf(op, excluded).name; prevBy[n] = (prevBy[n] || 0) + 1; });

    const people = {};
    opened.forEach((op) => {
        const { name, isBranch } = ownerOf(op, excluded);
        const p = people[name] || (people[name] = { name, isBranch, list: [], days: new Set(), branches: {}, types: {} });
        p.list.push(op);
        p.days.add(dayKey(op.pickupAt));
        p.branches[op.branch] = (p.branches[op.branch] || 0) + 1;
        p.types[op.type || 'Daily'] = (p.types[op.type || 'Daily'] || 0) + 1;
    });
    const rows = Object.values(people);
    const staff = rows.filter((p) => !p.isBranch);
    const total = rows.reduce((sum, p) => sum + p.list.length, 0);
    const staffTotal = staff.reduce((sum, p) => sum + p.list.length, 0);
    const avg = staff.length ? staffTotal / staff.length : 0;
    return {
        total,
        people: staff.length,
        average: Math.round(avg * 10) / 10,
        rows: rows.map((p) => {
            const n = p.list.length;
            const branches = Object.entries(p.branches).sort((a, b) => b[1] - a[1]);
            const index = !p.isBranch && avg > 0 ? n / avg : 0;
            return {
                name: p.name,
                isBranch: p.isBranch,
                opened: n,
                openNow: p.list.filter((op) => op.operationStatus === 'In-Progress').length,
                closed: p.list.filter((op) => op.operationStatus === 'Closed').length,
                days: p.days.size,
                perDay: Math.round((n / p.days.size) * 10) / 10,
                mainBranch: branches[0]?.[0] || '',
                branches,
                types: Object.entries(p.types).sort((a, b) => b[1] - a[1]),
                share: total ? Math.round((n / total) * 100) : 0,
                index,
                standing: p.isBranch ? { label: 'Branch', tone: 'neutral' } : standingOf(index),
                prevOpened: prevBy[p.name] || 0,
                change: change(n, prevBy[p.name] || 0),
                list: p.list,
            };
        }).sort((a, b) => (a.isBranch - b.isBranch) || b.opened - a.opened || a.name.localeCompare(b.name)),
    };
}

// (the Overview tab is THE Overview now: the engine's answer, pages/overview - owner, 1 Oct 2026)
